// src/components/mitchRoom/revenueAnalytics/CohortRetentionTable.js
import React, { useMemo, useState } from 'react';
import { Skeleton } from 'components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from 'components/ui/toggle-group';
import { formatPeriod } from 'utils/format';

// Continuous retention colour scale: hue interpolates red (0%) -> green (100%),
// opacity ramps up with retention so strong cohorts read as saturated cells.
const getRetentionColor = (rate) => {
  if (rate === null || rate === undefined) return 'transparent';
  const clamped = Math.max(0, Math.min(100, rate));
  const hue = (clamped / 100) * 120;
  const alpha = 0.35 + (clamped / 100) * 0.45;
  return `hsla(${hue.toFixed(0)}, 70%, 42%, ${alpha.toFixed(2)})`;
};

const getTextColor = (rate) => (rate >= 60 ? '#fff' : 'inherit');

const LEGEND_GRADIENT = `linear-gradient(to right, ${getRetentionColor(0)}, ${getRetentionColor(
  50
)}, ${getRetentionColor(100)})`;

const HEADER_TH =
  'sticky top-0 z-[3] whitespace-nowrap border-b border-borderGrey bg-white p-2 text-center text-token-base font-semibold';

export default function CohortRetentionTable({ data, maxPeriods = 12, loading }) {
  // 'pct' shows retention_rate, 'count' shows retained_count / cohort_size
  const [cellMode, setCellMode] = useState('pct');

  // Transform API data into table structure
  const { cohorts, periodHeaders, avgRetention, avgRetained } = useMemo(() => {
    if (!data || !data.cohorts || data.cohorts.length === 0) {
      return { cohorts: [], periodHeaders: [], avgRetention: {}, avgRetained: {} };
    }

    // Generate period headers (Month 0, 1, 2, ... maxPeriods)
    const headers = Array.from({ length: maxPeriods + 1 }, (_, i) => i);

    // Calculate average retention (and retained count) per period
    const periodTotals = {};
    const periodRetained = {};
    const periodCounts = {};

    data.cohorts.forEach((cohort) => {
      Object.entries(cohort.periods || {}).forEach(([period, values]) => {
        const periodNum = parseInt(period);
        if (!periodTotals[periodNum]) {
          periodTotals[periodNum] = 0;
          periodRetained[periodNum] = 0;
          periodCounts[periodNum] = 0;
        }
        periodTotals[periodNum] += values.retention_rate || 0;
        periodRetained[periodNum] += values.retained_count || 0;
        periodCounts[periodNum] += 1;
      });
    });

    const avgRet = {};
    const avgCnt = {};
    Object.keys(periodTotals).forEach((period) => {
      avgRet[period] = periodTotals[period] / periodCounts[period];
      avgCnt[period] = periodRetained[period] / periodCounts[period];
    });

    return {
      cohorts: data.cohorts,
      periodHeaders: headers,
      avgRetention: avgRet,
      avgRetained: avgCnt,
    };
  }, [data, maxPeriods]);

  if (loading && (!data || !data.cohorts || data.cohorts.length === 0)) {
    return (
      <div className="rounded-token-large border border-borderGrey bg-white p-4">
        <h3 className="mb-3 text-token-xl font-semibold">Cohort Retention</h3>
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (!cohorts || cohorts.length === 0) {
    return (
      <div className="rounded-token-large border border-borderGrey bg-white p-4">
        <h3 className="text-token-xl font-semibold">Cohort Retention</h3>
        <div className="flex h-[400px] items-center justify-center">
          <p className="text-token-md text-muted-foreground">No cohort data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-token-large border border-borderGrey bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-token-xl font-semibold">Cohort Retention</h3>
        <div className="flex items-center gap-3">
          <span className="text-token-sm text-muted-foreground">
            {cohorts.length} cohorts | Periods 0-{maxPeriods}
          </span>
          <ToggleGroup
            type="single"
            size="sm"
            value={cellMode}
            onValueChange={(v) => v && setCellMode(v)}
            aria-label="Cell display mode"
          >
            <ToggleGroupItem value="pct" aria-label="Show percentages">
              %
            </ToggleGroupItem>
            <ToggleGroupItem value="count" aria-label="Show retained counts">
              #
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>

      <div className="max-h-[500px] overflow-auto">
        <table className="w-full border-separate border-spacing-0 text-token-base">
          <thead>
            <tr>
              <th className={`${HEADER_TH} left-0 z-[4] min-w-[80px] text-left`}>Cohort</th>
              <th className={`${HEADER_TH} min-w-[56px]`}>Size</th>
              {periodHeaders.map((period) => (
                <th key={period} className={`${HEADER_TH} min-w-[48px]`}>
                  M{period}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cohorts.map((cohort) => (
              <tr key={cohort.cohort} className="group">
                <td className="sticky left-0 z-[1] whitespace-nowrap border-b border-borderGrey bg-white p-2 font-medium group-hover:bg-neutral-50">
                  {formatPeriod(cohort.cohort)}
                </td>
                <td className="border-b border-borderGrey p-2 text-center font-medium">
                  {cohort.cohort_size}
                </td>
                {periodHeaders.map((period) => {
                  const periodData = cohort.periods?.[period];
                  const rate = periodData?.retention_rate;
                  const retained = periodData?.retained_count;

                  if (rate === undefined || rate === null) {
                    return (
                      <td
                        key={period}
                        className="border-b border-borderGrey bg-neutral-100 p-2 text-center text-muted-foreground"
                      >
                        -
                      </td>
                    );
                  }

                  return (
                    <td
                      key={period}
                      title={`${formatPeriod(cohort.cohort)} · Month ${period}\nRetained: ${retained} / ${cohort.cohort_size}\nRate: ${rate.toFixed(1)}%`}
                      className="cursor-default border-b border-borderGrey p-2 text-center font-medium tabular-nums"
                      style={{
                        backgroundColor: getRetentionColor(rate),
                        color: getTextColor(rate),
                      }}
                    >
                      {cellMode === 'pct' ? `${rate.toFixed(0)}%` : retained}
                    </td>
                  );
                })}
              </tr>
            ))}

            {/* Average row */}
            <tr>
              <td className="sticky left-0 z-[1] border-b border-borderGrey bg-neutral-100 p-2 font-semibold">
                Avg
              </td>
              <td className="border-b border-borderGrey bg-neutral-100 p-2 text-center font-semibold">
                {Math.round(cohorts.reduce((sum, c) => sum + c.cohort_size, 0) / cohorts.length)}
              </td>
              {periodHeaders.map((period) => {
                const avg = avgRetention[period];
                if (avg === undefined || avg === null) {
                  return (
                    <td
                      key={period}
                      className="border-b border-borderGrey bg-neutral-100 p-2 text-center text-muted-foreground"
                    >
                      -
                    </td>
                  );
                }
                return (
                  <td
                    key={period}
                    className="border-b border-borderGrey bg-neutral-100 p-2 text-center font-semibold tabular-nums"
                  >
                    {cellMode === 'pct' ? `${avg.toFixed(0)}%` : Math.round(avgRetained[period])}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Continuous colour-scale legend */}
      <div className="mt-3 flex items-center justify-center gap-2">
        <span className="text-token-sm text-muted-foreground">0%</span>
        <div
          className="h-3 w-48 rounded-token-small"
          style={{ background: LEGEND_GRADIENT }}
          aria-hidden="true"
        />
        <span className="text-token-sm text-muted-foreground">100%</span>
      </div>
    </div>
  );
}
